// @ts-nocheck
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { motion } from "framer-motion";
import { LogIn, Loader2, UserPlus, Mail, ArrowRight, Eye, EyeOff, Check, X, Shield, Sparkles, MessageCircle, Clock, KeyRound, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const WHATSAPP_LINK = "https://wa.me/message/2YZPI7YOJPW4D1";
const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const Login = () => {
  const [mode, setMode] = useState<"login" | "signup" | "email-verification" | "confirmation" | "pending">("login");
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [emailVerificationCode, setEmailVerificationCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [loading, setLoading] = useState(false);
  const [foundMember, setFoundMember] = useState<{ id: string; name: string; photo_url?: string } | null>(null);
  const { signIn, signUp } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Password validation checks
  const passwordChecks = [
    { label: "8 أحرف على الأقل", pass: password.length >= 8, icon: "📏" },
    { label: "حرف كبير (A-Z)", pass: /[A-Z]/.test(password), icon: "🔠" },
    { label: "حرف صغير (a-z)", pass: /[a-z]/.test(password), icon: "🔡" },
    { label: "رقم (0-9)", pass: /\d/.test(password), icon: "🔢" },
    { label: "رمز (!@#$...)", pass: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(password), icon: "✨" },
  ];
  
  const passwordScore = passwordChecks.filter(c => c.pass).length;
  const isPasswordValid = passwordScore === 5;
  const isEnglishOnly = !/[^a-zA-Z\d!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~ ]/.test(password);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    // First, check if this is an admin login
    try {
      const adminResponse = await fetch(`${API_URL}/api/auth/admin-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      
      const adminData = await adminResponse.json();
      
      if (adminData.success && adminData.is_admin) {
        // Save admin token to localStorage
        localStorage.setItem("admin_token", adminData.token);
        localStorage.setItem("admin_user", JSON.stringify(adminData.user));
        setLoading(false);
        toast({ title: "مرحباً بك!", description: `أهلاً ${adminData.user.name}` });
        navigate("/admin");
        return;
      }
    } catch (e) {
      console.log("Admin check note:", e);
    }
    
    // Second, check if there's a pending registration in our database
    try {
      const pendingResponse = await fetch(`${API_URL}/api/auth/verify-pending-login?email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`, {
        method: "POST",
      });
      
      const pendingData = await pendingResponse.json();
      
      if (pendingData.valid) {
        // User has a pending registration - show family code entry
        setLoading(false);
        toast({ title: "مرحباً بك!", description: "يرجى إدخال كود فرد العائلة لتفعيل حسابك" });
        setMode("confirmation");
        return;
      }
    } catch (e) {
      // Continue with normal Supabase login
      console.log("Pending check note:", e);
    }
    
    // Try Supabase login
    const { error } = await signIn(email, password);
    setLoading(false);

    if (error) {
      // Check if error is due to email not confirmed
      if (error.message.includes("Email not confirmed")) {
        toast({
          title: "حسابك معلق",
          description: "يرجى إدخال كود فرد العائلة",
        });
        setMode("confirmation");
        return;
      }
      toast({
        title: "خطأ في تسجيل الدخول",
        description: "البريد الإلكتروني أو كلمة المرور غير صحيحة",
        variant: "destructive",
      });
    } else {
      // Check if user has a pending claim or needs to enter family code
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // Check member_claims table for this user
        const { data: claim } = await supabase
          .from("member_claims")
          .select("status, member_id")
          .eq("user_id", user.id)
          .single();
        
        if (!claim) {
          // User has no claim - show family code entry screen
          toast({ title: "مرحباً بك!", description: "يرجى إدخال كود فرد العائلة لتفعيل حسابك" });
          setMode("confirmation");
          return;
        } else if (claim.status === "pending") {
          // Claim is pending approval
          toast({ title: "حسابك قيد المراجعة", description: "يرجى انتظار موافقة المشرف" });
          setMode("pending");
          return;
        } else if (claim.status === "rejected") {
          // Claim was rejected
          toast({ title: "تم رفض طلبك", description: "يمكنك إعادة المحاولة بكود آخر", variant: "destructive" });
          setMode("confirmation");
          return;
        }
        // Claim is approved - proceed
        toast({ title: "مرحباً بك!", description: "تم تسجيل الدخول بنجاح" });
        navigate("/tree");
      }
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email confirmation
    if (email !== confirmEmail) {
      toast({ title: "البريد الإلكتروني غير متطابق", description: "تأكد من تطابق البريد الإلكتروني", variant: "destructive" });
      return;
    }
    
    // Validate English only
    if (!isEnglishOnly) {
      toast({ title: "كلمة المرور غير صالحة", description: "يجب أن تكون بالإنجليزية فقط (أحرف وأرقام ورموز)", variant: "destructive" });
      return;
    }
    
    // Validate password strength
    if (!isPasswordValid) {
      toast({ title: "كلمة المرور ضعيفة", description: "يجب أن تحتوي على 8 خانات على الأقل مع حرف كبير وصغير ورقم ورمز", variant: "destructive" });
      return;
    }
    
    // Validate password confirmation
    if (password !== confirmPassword) {
      toast({ title: "كلمة المرور غير متطابقة", description: "تأكد من تطابق كلمتي المرور", variant: "destructive" });
      return;
    }
    
    // Send email verification code first
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/send-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ title: "تم إرسال الكود!", description: "تحقق من بريدك الإلكتروني" });
        setMode("email-verification");
        startResendTimer();
      } else {
        toast({ title: "خطأ", description: data.message || "فشل في إرسال كود التحقق", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "خطأ", description: "فشل في إرسال كود التحقق، حاول مرة أخرى", variant: "destructive" });
    }
    setLoading(false);
  };

  const startResendTimer = () => {
    setResendTimer(60);
    const interval = setInterval(() => {
      setResendTimer(prev => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResendEmailCode = async () => {
    setSendingCode(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ title: "تم إعادة الإرسال!", description: "تحقق من بريدك الإلكتروني" });
        startResendTimer();
      } else {
        toast({ title: "خطأ", description: data.message || "فشل في إعادة الإرسال", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "خطأ", description: "فشل في إعادة الإرسال", variant: "destructive" });
    }
    setSendingCode(false);
  };

  const handleVerifyEmailCode = async () => {
    if (emailVerificationCode.length !== 6) {
      toast({ title: "أدخل الكود كاملاً", description: "يجب إدخال 6 أرقام", variant: "destructive" });
      return;
    }
    
    setVerifying(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/verify-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code: emailVerificationCode }),
      });
      
      const data = await response.json();
      
      if (data.verified) {
        // Save pending registration to our database (MongoDB)
        // لا نقوم بإنشاء حساب Supabase هنا - سيتم إنشاؤه بعد موافقة المشرف
        const saveResponse = await fetch(`${API_URL}/api/auth/save-pending-registration`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password }),
        });
        
        const saveData = await saveResponse.json();
        
        if (saveData.success) {
          toast({ title: "تم التحقق!", description: "أدخل كود فرد العائلة لإكمال التسجيل" });
          
          // Move to family code entry - NO Supabase signup here
          // الحساب سيُنشأ في Supabase بعد موافقة المشرف على طلب التفعيل
          setMode("confirmation");
        } else {
          toast({ title: "خطأ", description: saveData.message || "فشل في حفظ التسجيل", variant: "destructive" });
        }
      } else {
        toast({ title: "كود خاطئ", description: data.message || "كود التحقق غير صحيح", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "خطأ", description: "فشل في التحقق، حاول مرة أخرى", variant: "destructive" });
    }
    setVerifying(false);
  };

  const handleVerifyOtp = async () => {
    if (otpCode.length !== 6) {
      toast({ title: "أدخل الكود كاملاً", description: "يجب إدخال 6 أرقام/أحرف", variant: "destructive" });
      return;
    }
    setVerifying(true);
    
    // Check if this is a valid family code
    const { data: memberWithCode, error: memberError } = await supabase
      .from("family_members")
      .select("id, name, member_code, photo_url")
      .eq("member_code", otpCode.toUpperCase())
      .single();
    
    if (memberError || !memberWithCode) {
      setVerifying(false);
      toast({ title: "كود فرد العائلة غير صحيح", description: "تأكد من الكود الذي حصلت عليه من المشرف", variant: "destructive" });
      return;
    }
    
    // Store member info for display
    setFoundMember(memberWithCode);
    
    // Save linking request to backend (MongoDB) since user is not logged into Supabase yet
    try {
      const response = await fetch(`${API_URL}/api/auth/save-linking-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          email: email,
          password: password,
          member_id: memberWithCode.id,
          member_code: otpCode.toUpperCase(),
          member_name: memberWithCode.name,
        }),
      });
      
      const data = await response.json();
      
      setVerifying(false);
      
      if (data.success) {
        toast({ 
          title: `تم العثور على بيانات: ${memberWithCode.name}`, 
          description: "تم إرسال طلب الربط للمشرف، انتظر الموافقة" 
        });
        setMode("pending");
      } else {
        toast({ title: "خطأ", description: data.message || "فشل في إرسال طلب الربط", variant: "destructive" });
      }
    } catch (error) {
      setVerifying(false);
      toast({ title: "خطأ", description: "حدث خطأ أثناء إرسال الطلب، حاول مرة أخرى", variant: "destructive" });
    }
  };

  const handleResendCode = async () => {
    const { error } = await supabase.auth.resend({ type: "signup", email });
    if (error) {
      toast({ title: "خطأ", description: "تعذر إعادة إرسال الكود، حاول لاحقاً", variant: "destructive" });
    } else {
      toast({ title: "تم إعادة الإرسال", description: "تحقق من بريدك الإلكتروني" });
      setResendTimer(60);
      const interval = setInterval(() => {
        setResendTimer(prev => {
          if (prev <= 1) { clearInterval(interval); return 0; }
          return prev - 1;
        });
      }, 1000);
    }
  };

  // Email Verification Screen - Verify email with 6-digit code
  if (mode === "email-verification") {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 pt-20">
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-primary/5 blur-[120px]" />
          <div className="absolute bottom-1/4 left-1/4 w-[300px] h-[300px] rounded-full bg-accent/3 blur-[100px]" />
        </div>
        
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-md relative z-10">
          <Card className="glass-card border-primary/10 rounded-3xl modern-shadow overflow-hidden">
            <CardHeader className="text-center pb-4 pt-8">
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", delay: 0.2 }}
                className="mx-auto mb-5 p-5 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 w-fit border border-primary/20"
              >
                <Mail className="h-10 w-10 text-primary" />
              </motion.div>
              <CardTitle className="font-amiri text-3xl gold-text">تحقق من بريدك</CardTitle>
              <p className="text-muted-foreground mt-4 text-sm leading-relaxed">
                تم إرسال كود تحقق مكوّن من <span className="text-primary font-bold">6 أرقام</span> إلى
              </p>
              <p className="text-primary font-medium mt-2 text-base" dir="ltr">{email}</p>
            </CardHeader>
            <CardContent className="space-y-6 px-6 pb-8">
              {/* Code Input */}
              <div className="space-y-3">
                <Label className="text-sm font-medium text-center block">أدخل كود التحقق</Label>
                <div className="flex justify-center" dir="ltr">
                  <InputOTP maxLength={6} value={emailVerificationCode} onChange={setEmailVerificationCode}>
                    <InputOTPGroup className="gap-2">
                      {[0,1,2,3,4,5].map(i => (
                        <InputOTPSlot 
                          key={i} 
                          index={i} 
                          className="w-12 h-14 text-xl font-bold rounded-xl border-2 border-primary/20 bg-secondary/50 focus:border-primary transition-all"
                        />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>
              
              <Button
                className="w-full h-12 gold-gradient text-primary-foreground rounded-xl text-base font-semibold shadow-lg shadow-primary/20"
                onClick={handleVerifyEmailCode}
                disabled={verifying || emailVerificationCode.length !== 6}
                data-testid="verify-email-btn"
              >
                {verifying ? <Loader2 className="h-5 w-5 animate-spin ml-2" /> : <Shield className="h-5 w-5 ml-2" />}
                تأكيد الكود
              </Button>

              {/* Resend Code */}
              <div className="text-center">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-sm text-muted-foreground hover:text-primary rounded-xl"
                  onClick={handleResendEmailCode}
                  disabled={resendTimer > 0 || sendingCode}
                  data-testid="resend-email-code-btn"
                >
                  {sendingCode ? (
                    <Loader2 className="h-4 w-4 animate-spin ml-2" />
                  ) : resendTimer > 0 ? (
                    <span>إعادة الإرسال بعد <span className="text-primary font-bold">{resendTimer}</span> ثانية</span>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 ml-2" />
                      لم يصلك الكود؟ أعد الإرسال
                    </>
                  )}
                </Button>
              </div>

              {/* Note */}
              <div className="text-center p-3 rounded-xl bg-secondary/30 border border-primary/10">
                <p className="text-xs text-muted-foreground leading-relaxed">
                  💡 <span className="text-foreground font-medium">ملاحظة:</span> الكود صالح لمدة 10 دقائق فقط. تحقق من مجلد البريد غير المرغوب فيه.
                </p>
              </div>
              
              <Button
                variant="outline"
                className="w-full rounded-xl border-primary/20 hover:bg-primary/5"
                onClick={() => { setMode("signup"); setEmailVerificationCode(""); }}
              >
                <ArrowRight className="h-4 w-4 ml-2" />
                تعديل البريد الإلكتروني
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Pending Approval Screen - After submitting activation request
  if (mode === "pending") {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 pt-20">
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-primary/5 blur-[120px]" />
          <div className="absolute bottom-1/4 left-1/4 w-[300px] h-[300px] rounded-full bg-accent/3 blur-[100px]" />
        </div>
        
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-md relative z-10">
          <Card className="glass-card border-primary/10 rounded-3xl modern-shadow overflow-hidden">
            <CardHeader className="text-center pb-4 pt-8">
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", delay: 0.2 }}
                className="mx-auto mb-5 p-5 rounded-2xl bg-gradient-to-br from-green-500/20 to-green-500/5 w-fit border border-green-500/20"
              >
                <Clock className="h-10 w-10 text-green-500" />
              </motion.div>
              <CardTitle className="font-amiri text-3xl gold-text">تم إرسال طلبك!</CardTitle>
              <p className="text-muted-foreground mt-4 text-sm leading-relaxed">
                تم إرسال طلب تفعيل حسابك بنجاح للمشرف
              </p>
            </CardHeader>
            <CardContent className="space-y-6 px-6 pb-8">
              <div className="p-4 rounded-2xl bg-green-500/10 border border-green-500/20">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-full bg-green-500/20">
                    <Shield className="h-5 w-5 text-green-500" />
                  </div>
                  <span className="font-semibold text-green-500">طلبك قيد المراجعة</span>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  سيقوم المشرف بمراجعة طلبك والموافقة عليه قريباً. بعد الموافقة ستتمكن من تسجيل الدخول.
                </p>
              </div>

              <div className="text-center p-3 rounded-xl bg-secondary/30 border border-primary/10">
                <p className="text-xs text-muted-foreground leading-relaxed">
                  💡 <span className="text-foreground font-medium">ملاحظة:</span> إذا تأخرت الموافقة، يمكنك التواصل مع المشرف عبر الواتساب
                </p>
              </div>

              <motion.a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex items-center justify-center gap-3 w-full h-12 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-xl text-base font-semibold shadow-lg shadow-green-500/30 hover:shadow-green-500/50 transition-all"
              >
                <MessageCircle className="h-5 w-5" />
                تواصل عبر الواتساب
              </motion.a>
              
              <Button
                variant="outline"
                className="w-full rounded-xl border-primary/20 hover:bg-primary/5"
                onClick={() => { setMode("login"); setPassword(""); setOtpCode(""); }}
              >
                <ArrowRight className="h-4 w-4 ml-2" />
                العودة لتسجيل الدخول
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // OTP Confirmation Screen - Enter family member code
  if (mode === "confirmation") {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 pt-20">
        {/* Background Effects */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-primary/5 blur-[120px]" />
          <div className="absolute bottom-1/4 left-1/4 w-[300px] h-[300px] rounded-full bg-accent/3 blur-[100px]" />
        </div>
        
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-md relative z-10">
          <Card className="glass-card border-primary/10 rounded-3xl modern-shadow overflow-hidden">
            <CardHeader className="text-center pb-4 pt-8">
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", delay: 0.2 }}
                className="mx-auto mb-5 p-5 rounded-2xl bg-gradient-to-br from-green-500/20 to-green-500/5 w-fit border border-green-500/20"
              >
                <Check className="h-10 w-10 text-green-500" />
              </motion.div>
              <CardTitle className="font-amiri text-3xl gold-text">تم إنشاء حسابك!</CardTitle>
              <p className="text-muted-foreground mt-4 text-sm leading-relaxed">
                تم إنشاء حسابك بنجاح. الآن أدخل كود فرد العائلة الخاص بك
              </p>
              <p className="text-primary font-medium mt-2 text-base" dir="ltr">{email}</p>
            </CardHeader>
            <CardContent className="space-y-6 px-6 pb-8">
              {/* Important Note - This is NOT activation code */}
              <div className="p-4 rounded-2xl bg-blue-500/10 border border-blue-500/20">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-full bg-blue-500/20">
                    <KeyRound className="h-5 w-5 text-blue-500" />
                  </div>
                  <span className="font-semibold text-blue-500">كود فرد العائلة</span>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  <span className="text-foreground font-medium">⚠️ هذا ليس كود تفعيل!</span> هذا هو الكود الخاص بك كفرد من العائلة. إذا لم يكن لديك كود، يمكنك الحصول عليه من المشرف.
                </p>
              </div>

              {/* WhatsApp Contact Button */}
              <motion.a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex items-center justify-center gap-3 w-full h-12 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-xl text-sm font-semibold shadow-lg shadow-green-500/30 hover:shadow-green-500/50 transition-all"
                data-testid="whatsapp-contact-btn"
              >
                <MessageCircle className="h-5 w-5" />
                احصل على كودك من المشرف عبر الواتساب
              </motion.a>

              {/* Divider */}
              <div className="flex items-center gap-4">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent to-primary/20" />
                <span className="text-xs text-muted-foreground">إذا لديك الكود</span>
                <div className="flex-1 h-px bg-gradient-to-l from-transparent to-primary/20" />
              </div>

              {/* Family Member Code Input */}
              <div className="space-y-3">
                <Label className="text-sm font-medium text-center block">أدخل كود فرد العائلة (6 أحرف/أرقام)</Label>
                <div className="flex justify-center" dir="ltr">
                  <InputOTP maxLength={6} value={otpCode} onChange={(val) => setOtpCode(val.toUpperCase())}>
                    <InputOTPGroup className="gap-2">
                      {[0,1,2,3,4,5].map(i => (
                        <InputOTPSlot 
                          key={i} 
                          index={i} 
                          className="w-12 h-14 text-xl font-bold rounded-xl border-2 border-primary/20 bg-secondary/50 focus:border-primary transition-all uppercase"
                        />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <p className="text-xs text-center text-muted-foreground">
                  مثال: <span className="text-primary font-mono">ABC123</span>
                </p>
              </div>
              
              <Button
                className="w-full h-12 gold-gradient text-primary-foreground rounded-xl text-base font-semibold shadow-lg shadow-primary/20"
                onClick={handleVerifyOtp}
                disabled={verifying || otpCode.length !== 6}
                data-testid="verify-otp-btn"
              >
                {verifying ? <Loader2 className="h-5 w-5 animate-spin ml-2" /> : <Shield className="h-5 w-5 ml-2" />}
                ربط حسابي بالعائلة
              </Button>

              {/* Skip Option */}
              <div className="text-center p-3 rounded-xl bg-orange-500/5 border border-orange-500/20">
                <p className="text-xs text-muted-foreground leading-relaxed mb-2">
                  ⏳ <span className="text-orange-500 font-medium">لا تملك الكود الآن؟</span> يمكنك إدخاله لاحقاً
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-orange-500 hover:text-orange-400"
                  onClick={() => {
                    toast({ title: "حسابك معلق", description: "يمكنك إدخال كود العائلة عند تسجيل الدخول لاحقاً" });
                    setMode("login");
                    setPassword("");
                    setOtpCode("");
                  }}
                >
                  تخطي وإدخال الكود لاحقاً
                </Button>
              </div>
              
              <Button
                variant="outline"
                className="w-full rounded-xl border-primary/20 hover:bg-primary/5"
                onClick={() => { setMode("login"); setPassword(""); setOtpCode(""); }}
              >
                <ArrowRight className="h-4 w-4 ml-2" />
                العودة لتسجيل الدخول
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Login / Signup Screen
  return (
    <div className="min-h-screen flex items-center justify-center px-4 pt-20 pb-10">
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute bottom-1/4 left-1/4 w-[300px] h-[300px] rounded-full bg-accent/3 blur-[100px]" />
      </div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        <Card className="glass-card border-primary/10 rounded-3xl modern-shadow overflow-hidden">
          <CardHeader className="text-center pb-4 pt-8">
            <motion.div 
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", delay: 0.2 }}
              className="mx-auto mb-5 p-5 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 w-fit border border-primary/20"
            >
              {mode === "login" ? (
                <LogIn className="h-10 w-10 text-primary" />
              ) : (
                <UserPlus className="h-10 w-10 text-primary" />
              )}
            </motion.div>
            <CardTitle className="font-amiri text-3xl gold-text">
              {mode === "login" ? "دخول أفراد العائلة" : "إنشاء حساب جديد"}
            </CardTitle>
            <p className="text-muted-foreground mt-3 text-sm leading-relaxed max-w-xs mx-auto">
              {mode === "login"
                ? "أدخل بيانات حسابك للوصول لمعلومات العائلة الكاملة"
                : "أنشئ حسابك للانضمام لشجرة عائلة أبوليلة"
              }
            </p>
          </CardHeader>
          
          <CardContent className="px-6 pb-8">
            <form onSubmit={mode === "login" ? handleLogin : handleSignup} className="space-y-5">
              {/* Email Field */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">البريد الإلكتروني</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="example@email.com"
                  className="h-12 bg-secondary/50 border-primary/20 rounded-xl text-base"
                  dir="ltr"
                  required
                  data-testid="email-input"
                />
              </div>
              
              {/* Confirm Email Field - Only for Signup */}
              {mode === "signup" && (
                <div className="space-y-2">
                  <Label htmlFor="confirmEmail" className="text-sm font-medium">تأكيد البريد الإلكتروني</Label>
                  <Input
                    id="confirmEmail"
                    type="email"
                    value={confirmEmail}
                    onChange={(e) => setConfirmEmail(e.target.value)}
                    placeholder="أعد كتابة البريد الإلكتروني"
                    className={`h-12 bg-secondary/50 border-primary/20 rounded-xl text-base ${confirmEmail && email !== confirmEmail ? 'border-destructive' : confirmEmail && email === confirmEmail ? 'border-green-500' : ''}`}
                    dir="ltr"
                    required
                    data-testid="confirm-email-input"
                  />
                  {confirmEmail && (
                    <p className={`text-xs flex items-center gap-1 ${email === confirmEmail ? 'text-green-500' : 'text-destructive'}`}>
                      {email === confirmEmail ? (
                        <><Check className="h-3 w-3" /> البريد الإلكتروني متطابق</>
                      ) : (
                        <><X className="h-3 w-3" /> البريد الإلكتروني غير متطابق</>
                      )}
                    </p>
                  )}
                </div>
              )}
              
              {/* Password Field */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">كلمة المرور</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="h-12 bg-secondary/50 border-primary/20 rounded-xl text-base pl-12"
                    dir="ltr"
                    required
                    minLength={8}
                    data-testid="password-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute left-1 top-1/2 -translate-y-1/2 h-10 w-10 text-muted-foreground hover:text-foreground hover:bg-transparent"
                    data-testid="toggle-password-btn"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </Button>
                </div>
                
                {/* Password Strength Indicator - Only for Signup */}
                {mode === "signup" && password && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="space-y-3 mt-3 p-4 rounded-xl bg-secondary/30 border border-primary/10"
                  >
                    {/* Strength Bar */}
                    <div className="space-y-2">
                      <div className="flex gap-1">
                        {[0,1,2,3,4].map(i => (
                          <div 
                            key={i} 
                            className={`h-2 flex-1 rounded-full transition-all duration-300 ${
                              i < passwordScore 
                                ? passwordScore <= 2 ? 'bg-destructive' 
                                  : passwordScore <= 3 ? 'bg-orange-500' 
                                  : passwordScore <= 4 ? 'bg-yellow-500' 
                                  : 'bg-green-500'
                                : 'bg-muted'
                            }`} 
                          />
                        ))}
                      </div>
                      <div className="flex justify-between items-center">
                        <span className={`text-xs font-semibold ${
                          passwordScore <= 2 ? 'text-destructive' 
                            : passwordScore <= 3 ? 'text-orange-500' 
                            : passwordScore <= 4 ? 'text-yellow-500' 
                            : 'text-green-500'
                        }`}>
                          {passwordScore <= 2 ? 'ضعيفة' : passwordScore <= 3 ? 'متوسطة' : passwordScore <= 4 ? 'جيدة' : 'قوية ✓'}
                        </span>
                        {!isEnglishOnly && (
                          <span className="text-xs text-destructive">⚠️ إنجليزي فقط</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Requirements Checklist */}
                    <div className="grid grid-cols-2 gap-2">
                      {passwordChecks.map((check, i) => (
                        <motion.div 
                          key={i}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.05 }}
                          className={`flex items-center gap-2 text-xs p-2 rounded-lg transition-all ${
                            check.pass ? 'bg-green-500/10 text-green-500' : 'bg-muted/50 text-muted-foreground'
                          }`}
                        >
                          {check.pass ? (
                            <Check className="h-3.5 w-3.5 shrink-0" />
                          ) : (
                            <X className="h-3.5 w-3.5 shrink-0 opacity-50" />
                          )}
                          <span>{check.label}</span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </div>
              
              {/* Confirm Password Field - Only for Signup */}
              {mode === "signup" && (
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-sm font-medium">تأكيد كلمة المرور</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="أعد كتابة كلمة المرور"
                      className={`h-12 bg-secondary/50 border-primary/20 rounded-xl text-base pl-12 ${
                        confirmPassword && password !== confirmPassword ? 'border-destructive' 
                          : confirmPassword && password === confirmPassword ? 'border-green-500' : ''
                      }`}
                      dir="ltr"
                      required
                      minLength={8}
                      data-testid="confirm-password-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute left-1 top-1/2 -translate-y-1/2 h-10 w-10 text-muted-foreground hover:text-foreground hover:bg-transparent"
                      data-testid="toggle-confirm-password-btn"
                    >
                      {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </Button>
                  </div>
                  {confirmPassword && (
                    <p className={`text-xs flex items-center gap-1 ${password === confirmPassword ? 'text-green-500' : 'text-destructive'}`}>
                      {password === confirmPassword ? (
                        <><Check className="h-3 w-3" /> كلمة المرور متطابقة</>
                      ) : (
                        <><X className="h-3 w-3" /> كلمة المرور غير متطابقة</>
                      )}
                    </p>
                  )}
                </div>
              )}
              
              {/* Submit Button */}
              <Button 
                type="submit" 
                className="w-full h-12 gold-gradient text-primary-foreground rounded-xl text-base font-semibold shadow-lg shadow-primary/20 mt-6" 
                disabled={loading || (mode === "signup" && (!isPasswordValid || email !== confirmEmail || password !== confirmPassword))}
                data-testid="submit-btn"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin ml-2" /> : <Sparkles className="h-5 w-5 ml-2" />}
                {mode === "login" ? "تسجيل الدخول" : "إنشاء الحساب"}
              </Button>
              
              {/* Forgot Password Link */}
              {mode === "login" && (
                <div className="text-center">
                  <Link to="/reset-password" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    نسيت كلمة المرور؟
                  </Link>
                </div>
              )}
            </form>

            {/* Switch Mode */}
            <div className="mt-6 pt-6 border-t border-primary/10 text-center">
              <button
                type="button"
                className="text-sm text-primary hover:underline font-medium"
                onClick={() => {
                  setMode(mode === "login" ? "signup" : "login");
                  setConfirmEmail("");
                  setConfirmPassword("");
                }}
                data-testid="switch-mode-btn"
              >
                {mode === "login" ? (
                  <>ليس لديك حساب؟ <span className="font-bold">سجّل الآن</span></>
                ) : (
                  <>لديك حساب؟ <span className="font-bold">سجّل دخولك</span></>
                )}
              </button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default Login;
